/**
 * Разбор результата занятия.
 *
 * Единица режима — слово пользователя, поэтому нажатия относятся к элементам
 * упражнения: ошибка в середине строки достаётся тому слову, в котором
 * сделана.
 *
 * Итоги остаются на устройстве: слой синхронизации отбрасывает единицы этого
 * режима по приставке идентификатора (ТЗ §11.2).
 */

import type {
  KeystrokeEvent,
  MasteryStat,
  MasteryUnit,
  TypingError,
  UnitOutcome,
} from '@type2x/shared-contracts';
import { toMasteryUnitId } from '@type2x/shared-contracts';
import { collectSegmentOutcomes } from '@type2x/typing-engine';
import { applyUnitOutcomes, type LearningConfig } from '@type2x/learning-engine';
import type { Exercise } from '@type2x/exercise-generator';

/** Сопоставляет нажатия элементам упражнения. */
export function collectCustomOutcomes(
  exercise: Exercise,
  events: readonly KeystrokeEvent[],
  errors: readonly TypingError[],
): UnitOutcome[] {
  const items = exercise.items;
  const merged = new Map<string, UnitOutcome>();

  for (const outcome of collectSegmentOutcomes(
    items.map((item) => item.text),
    events,
    errors,
  )) {
    const unitId = items[outcome.index]?.unitId;
    if (unitId === undefined || unitId === null) continue;

    const previous = merged.get(unitId);
    merged.set(unitId, {
      unitId: toMasteryUnitId(unitId),
      attempts: (previous?.attempts ?? 0) + outcome.attempts,
      errors: (previous?.errors ?? 0) + outcome.errors,
      corrections: (previous?.corrections ?? 0) + outcome.corrections,
      // Показ одного и того же слова повторяется редко: медиана последнего
      // показа ближе к текущему состоянию, чем среднее по всем.
      medianLatencyMs: outcome.medianLatencyMs,
    });
  }

  return [...merged.values()];
}

/** Обновляет статистику слов пользователя по итогам занятия. */
export function applyCustomOutcomes(
  units: readonly MasteryUnit[],
  stats: Readonly<Record<string, MasteryStat>>,
  outcomes: readonly UnitOutcome[],
  config: LearningConfig,
  nowIso: string,
): MasteryStat[] {
  return applyUnitOutcomes(units, stats, outcomes, config, nowIso);
}
